import { IBudgetVersionService } from "../../../../models/budgetV2";
import { formatNumberToCurrency } from "../../../../utils/stringUtils";
import { useIntl } from "react-intl";

interface IManagerServiceProps {
  service: IBudgetVersionService;
  serviceIndex: number;
}

export const ManagerService = (props: IManagerServiceProps) => {
  const intl = useIntl();

  const calculatedSubtotal = () => {
    return (props.service.items ?? []).reduce((sum, item) => {
      if (item.isCovered === true || item.removed === true) return sum;
      return sum + (item.quantity ?? 0) * (item.value ?? 0) * (1 - (item.discount ?? 0) / 100);
    }, 0);
  };

  const observation = props.service.observation;
  const hasObservation = observation && typeof observation === 'string' && observation.trim() !== '';

  return (
    <div className={`border border-gray-200 rounded-md my-6`}>
      <div className='display-flex align-items-center padding-10'>
        <p className='text-medium text-size-16 margin-right-10'>{props.service.name?.trim() || 'Sob medida'}</p>
      </div>
      <div className="overflow-x-auto">
        <table
          className="table table-layout-fixed table-column-overflow-hidden table-head-filled mb-3 w-full [&_td]:border-0 [&_th]:border-0 [&_tbody_tr]:border-0 [&_tbody_tr]:border-b-0 [&_thead_tr]:border-0 [&_thead_tr]:border-b-0 [&_tbody_tr_td]:border-0 [&_tbody_tr_td]:border-b-0 [&_thead_tr_th]:border-0 [&_thead_tr_th]:border-b-0"
          style={{ borderCollapse: 'separate', borderSpacing: 0 }}
        >
          <thead>
            <tr className="border-0 !border-b-0" style={{ borderBottom: 'none' }}>
              <th className="w-[5%] border-0 !border-b-0 " style={{ borderBottom: 'none' }}>
                <span className="font-semibold text-size-12 text-gray-600">{intl.formatMessage({ id: 'intl-msg:budgets.ref' })}</span>
              </th>
              <th className="w-[23.75%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                <span className="font-semibold text-size-12 text-gray-600">{intl.formatMessage({ id: 'intl-msg:budgets.description' })}</span>
              </th>
              <th className="w-[23.75%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                <span className="font-semibold text-size-12 text-gray-600">{intl.formatMessage({ id: 'intl-msg:budgets.quantity' })}</span>
              </th>
              <th className="w-[23.75%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                <span className="font-semibold text-size-12 text-gray-600">{intl.formatMessage({ id: 'intl-msg:budgets.discount' })}</span>
              </th>
              <th className="w-[23.75%] border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                <span className="font-semibold text-size-12 text-gray-600">{intl.formatMessage({ id: 'intl-msg:budgets.subtotal' })}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {(props.service.items ?? []).map((item, index) => {
              const isRemoved = item.removed === true;
              const isCovered = item.isCovered === true;
              return (
                <tr className="border-0 !border-b-0 h-[50px]" style={{ borderBottom: 'none' }}>
                  <td className="border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                    {item.ref}
                  </td>
                  <td className="border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                    <div
                      className={`text-size-14 ${isRemoved ? '!bg-red-50' : ''}`}
                      style={isRemoved ? { backgroundColor: '#fef2f2', padding: '8px', borderRadius: '4px' } : { padding: '8px' }}
                    >
                      {item.description}
                    </div>
                  </td>
                  <td className="border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                    <div
                      className={`text-size-14 ${isRemoved ? '!bg-red-50' : ''}`}
                      style={isRemoved ? { backgroundColor: '#fef2f2', padding: '8px', borderRadius: '4px' } : { padding: '8px' }}
                    >
                      {item.quantity}
                    </div>
                  </td>
                  <td className="border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                    <div
                      className={`text-size-14 ${isRemoved ? '!bg-red-50' : ''} ${isCovered ? 'opacity-60 bg-gray-100' : ''}`}
                      style={
                        isRemoved
                          ? { backgroundColor: '#fef2f2', padding: '8px', borderRadius: '4px' }
                          : isCovered ? { padding: '8px', borderRadius: '4px', backgroundColor: '#f3f4f6' } : { padding: '8px' }
                      }
                    >
                      {item.discount}
                    </div>
                  </td>
                  <td className="border-0 !border-b-0" style={{ borderBottom: 'none' }}>
                    <div
                      className={`text-size-14 ${isRemoved ? '!bg-red-50' : ''} ${isCovered ? 'opacity-60 bg-gray-100' : ''}`}
                      style={
                        isRemoved
                          ? { backgroundColor: '#fef2f2', padding: '8px', borderRadius: '4px' }
                          : isCovered
                            ? { padding: '8px', borderRadius: '4px', backgroundColor: '#f3f4f6' }
                            : { padding: '8px' }
                      }
                    >
                      {formatNumberToCurrency(isCovered ? 0 : (item.quantity ?? 0) * (item.value ?? 0) * (1 - (item.discount ?? 0) / 100))}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
          <tfoot className=''>
            <tr className="w-full border-0 !border-b-0" style={{ borderBottom: 'none' }}>
              <td className=" text-right" colSpan={8} style={{ borderTop: '1px solid #e9e9e9' }}>
                <span className="text-gray-600 mr-2 text-size-16 ">{intl.formatMessage({ id: 'intl-msg:budgets.serviceSubtotal' })}:</span>
                <span className="text-gray-600 pr-2 text-size-16">{formatNumberToCurrency(calculatedSubtotal())}</span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {hasObservation && observation && (
        <div className="mb-4">
          <div className="font-bold mb-2 text-size-12 text-gray-500">{intl.formatMessage({ id: 'intl-msg:budgets.observation' })}</div>
          <div className="text-size-14 bg-white p-3 rounded border border-gray-200">
            {observation}
          </div>
        </div>
      )}
    </div>
  );
};